import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Navigation, RefreshCw, ChefHat, Wifi } from 'lucide-react';
import { ONLINE_KITCHENS_URL, NEARBY_KITCHENS_URL } from '../../config/api';
import './BrowseKitchens.css';

interface OnlineKitchen {
  kitchen_id: number;
  kitchen_name: string;
  kitchen_photo: string;
  kitchen_type: string;
  kitchen_category: string;
  city: string;
  state: string;
  country: string;
  status: boolean;
  made_by: string;
  distance_km?: number;
}

interface BrowseKitchensProps {
  onSelectKitchen: (kitchenId: number | null, kitchenName: string | null) => void;
  selectedKitchenId: number | null;
}

type ViewMode = 'all' | 'nearby';
type RadiusKm = 5 | 10 | 25;

const RADIUS_OPTIONS: RadiusKm[] = [5, 10, 25];

const BrowseKitchens: React.FC<BrowseKitchensProps> = ({ onSelectKitchen, selectedKitchenId }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [kitchens, setKitchens] = useState<OnlineKitchen[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [radiusKm, setRadiusKm] = useState<RadiusKm>(10);
  const [locationDenied, setLocationDenied] = useState(false);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);

  const fetchAllKitchens = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(ONLINE_KITCHENS_URL, {
        headers: { accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: OnlineKitchen[] = await res.json();
      setKitchens(Array.isArray(data) ? data : []);
    } catch {
      setError('Could not load kitchens. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchNearbyKitchens = useCallback(async (lat: number, lng: number, radius: RadiusKm) => {
    setIsLoading(true);
    setError(null);
    try {
      const url = `${NEARBY_KITCHENS_URL}?latitude=${lat}&longitude=${lng}&radius_km=${radius}`;
      const res = await fetch(url, { headers: { accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list: OnlineKitchen[] = Array.isArray(data?.kitchens) ? data.kitchens : [];
      setKitchens(list);
      if (list.length === 0) {
        setError(`No kitchens found within ${radius} km. Try increasing the radius.`);
      }
    } catch {
      setError('Could not load nearby kitchens. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const requestLocation = useCallback(() => {
    setError(null);
    setLocationDenied(false);
    if (!navigator.geolocation) {
      setError('Your browser does not support location detection. Showing all online kitchens instead.');
      setViewMode('all');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserCoords(coords);
        fetchNearbyKitchens(coords.lat, coords.lng, radiusKm);
      },
      () => {
        setLocationDenied(true);
        setViewMode('all');
      },
      { timeout: 10000 }
    );
  }, [fetchNearbyKitchens, radiusKm]);

  useEffect(() => {
    fetchAllKitchens();
  }, [fetchAllKitchens]);

  useEffect(() => {
    if (viewMode === 'all') {
      fetchAllKitchens();
    } else {
      if (userCoords) {
        fetchNearbyKitchens(userCoords.lat, userCoords.lng, radiusKm);
      } else {
        requestLocation();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  useEffect(() => {
    if (viewMode === 'nearby' && userCoords) {
      fetchNearbyKitchens(userCoords.lat, userCoords.lng, radiusKm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radiusKm]);

  const handleKitchenClick = (kitchenId: number, kitchenName: string) => {
    const isDeselecting = selectedKitchenId === kitchenId;
    const next = isDeselecting ? null : kitchenId;
    onSelectKitchen(next, isDeselecting ? null : kitchenName);
    if (next !== null) {
      setTimeout(() => {
        const mealsSection = document.getElementById('featured-meals');
        if (mealsSection) {
          mealsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 80);
    }
  };

  return (
    <section className="browse-kitchens">
      <div className="bk-inner">
        {/* Header */}
        <div className="bk-header">
          <div className="bk-title-block">
            <h2 className="bk-title">Browse Kitchens</h2>
            <p className="bk-subtitle">Discover home kitchens cooking near you right now</p>
          </div>

          {/* View toggle */}
          <div className="bk-toggle">
            <button
              className={`bk-toggle-btn ${viewMode === 'all' ? 'active' : ''}`}
              onClick={() => setViewMode('all')}
            >
              <Wifi size={15} />
              All Online
            </button>
            <button
              className={`bk-toggle-btn ${viewMode === 'nearby' ? 'active' : ''}`}
              onClick={() => setViewMode('nearby')}
            >
              <Navigation size={15} />
              Near Me
            </button>
          </div>
        </div>

        {/* Nearby controls */}
        {viewMode === 'nearby' && (
          <div className="bk-nearby-controls">
            <span className="bk-radius-label">Radius:</span>
            {RADIUS_OPTIONS.map(r => (
              <button
                key={r}
                className={`bk-radius-btn ${radiusKm === r ? 'active' : ''}`}
                onClick={() => setRadiusKm(r)}
              >
                {r} km
              </button>
            ))}
            <button
              className="bk-refresh-btn"
              onClick={() => userCoords ? fetchNearbyKitchens(userCoords.lat, userCoords.lng, radiusKm) : requestLocation()}
              disabled={isLoading}
              title="Refresh"
            >
              <RefreshCw size={14} className={isLoading ? 'bk-spin' : ''} />
            </button>
          </div>
        )}

        {/* Location denied notice */}
        {locationDenied && (
          <div className="bk-notice">
            <MapPin size={16} />
            <span>Location access was denied. Showing all online kitchens instead. Enable location in your browser settings to use the Near Me feature.</span>
          </div>
        )}

        {/* Active filter banner */}
        {selectedKitchenId !== null && (
          <div className="bk-filter-banner">
            <span>
              Showing food from <strong>{kitchens.find(k => k.kitchen_id === selectedKitchenId)?.kitchen_name || `Kitchen #${selectedKitchenId}`}</strong>
            </span>
            <button className="bk-clear-filter" onClick={() => onSelectKitchen(null, null)}>
              Clear filter
            </button>
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="bk-loading">
            <div className="bk-spinner" />
            <span>Loading kitchens...</span>
          </div>
        )}

        {/* Error (non-blocking) */}
        {error && !isLoading && (
          <div className="bk-error">{error}</div>
        )}

        {/* Kitchen cards grid */}
        {!isLoading && kitchens.length > 0 && (
          <div className="bk-grid">
            {kitchens.map(kitchen => (
              <button
                key={kitchen.kitchen_id}
                className={`bk-card ${selectedKitchenId === kitchen.kitchen_id ? 'selected' : ''}`}
                onClick={() => handleKitchenClick(kitchen.kitchen_id, kitchen.kitchen_name)}
              >
                <div className="bk-card-img-wrap">
                  {kitchen.kitchen_photo ? (
                    <img
                      src={kitchen.kitchen_photo}
                      alt={kitchen.kitchen_name}
                      className="bk-card-img"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        (e.target as HTMLImageElement).nextElementSibling?.classList.remove('bk-img-fallback--hidden');
                      }}
                    />
                  ) : null}
                  <div className={`bk-img-fallback ${kitchen.kitchen_photo ? 'bk-img-fallback--hidden' : ''}`}>
                    <ChefHat size={32} />
                  </div>
                  <span className="bk-status-dot" title="Online" />
                  {kitchen.distance_km !== undefined && (
                    <span className="bk-distance-badge">
                      <MapPin size={10} />
                      {kitchen.distance_km === 0 ? '<1 km' : `${kitchen.distance_km.toFixed(1)} km`}
                    </span>
                  )}
                </div>

                <div className="bk-card-body">
                  <p className="bk-card-name">{kitchen.kitchen_name}</p>
                  <p className="bk-card-category">{kitchen.kitchen_category}</p>
                  <p className="bk-card-city">
                    <MapPin size={11} />
                    {[kitchen.city, kitchen.state].filter(Boolean).join(', ')}
                  </p>
                </div>

                {selectedKitchenId === kitchen.kitchen_id && (
                  <div className="bk-card-selected-label">Viewing food</div>
                )}
              </button>
            ))}
          </div>
        )}

        {!isLoading && !error && kitchens.length === 0 && (
          <div className="bk-empty">
            <ChefHat size={48} />
            <p>No kitchens online right now. Check back soon!</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default BrowseKitchens;
